import React, { useContext, useEffect, useState } from 'react';
import { Button, Modal, NativeSelect } from '@material-ui/core';

import { fbmApi as api } from 'api/fbm';
import { procurementApi as procureApi } from 'api/procurement';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FormComponent, Notifications } from 'shared';
import dayjs from 'utils/dayjsConfig';

// The modal to add items to a purchase order
function AddItemModal({ asnID, closeModal, fbmAccount, getRows, items, parentRow }) {
    const [error, setError] = useState(null);

    const formFields = [
        {
            name: 'sku',
            inputElement: 'autocomplete',
            label: 'Item',
            gridValue: 12,
            inputProps: { required: true, opts: items },
        },
        {
            name: 'qty',
            inputElement: 'textField',
            label: 'Num of Cases',
            gridValue: 2,
            inputProps: { required: true },
        },
        {
            name: 'csPallet',
            inputElement: 'textField',
            label: 'Cases per pallet',
            gridValue: 2,
            inputProps: { required: true },
        },
        {
            name: 'numOfPallets',
            inputElement: 'textField',
            label: 'Num Of Pallets',
            gridValue: 2,
            inputProps: { type: 'number', integer: true },
        },
        {
            name: 'requireInspectionUponReceipt',
            inputElement: 'checkbox',
            label: 'Require Inspection',
            gridValue: 2,
        },
        { name: 'expectedExpirationDate', inputElement: 'date', label: 'Expected Expiration Date', gridValue: 6 },
    ];

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '700px', margin: '10% auto', padding: '10px' }}>
                <FormComponent
                    formFields={formFields}
                    onSubmit={async (formData) => {
                        const items = {};
                        items.items = [formData];
                        items.fbmAccount = fbmAccount.fbmAccountName;

                        const response = await api.addItemToASN(asnID, items);
                        if (response.status === false) {
                            let message = 'Error adding item';
                            if (response.msg) {
                                message += `: ${response.msg}`;
                            }
                            setError(`Error updating ${message}`);
                            return;
                        }

                        getRows();
                        closeModal();
                    }}
                />
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </div>
        </Modal>
    );
}

// The table to show items
function ViewItemsTable({ fbmAccount, onEditRow, parentRow, setExpandedRow }) {
    const [asnID, setASNID] = useState(parentRow.original.id);
    const [rows, setRows] = useState([]);
    const [addItemModalOpen, setAddItemModalOpen] = useState(false);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [items, setItems] = useState([]);
    const [itemsAutocomplete, setItemsAutocomplete] = useState([]);

    const getItems = async () => {
        const response = await api.getItems(fbmAccount.fbmAccountName);
        if (response.status === false) {
            setNotification({ text: `No entries found ${response.msg}`, level: 'error' });
            setItems([]);
            return;
        }
        const rows = response.data?.rows ?? [];
        setItems(rows);

        const itemsAsOpts = rows.map((item) => ({
            text: `SKU ${item.thirdPartyItemID}: ${item.name}`,
            value: item.thirdPartyItemID,
        }));
        setItemsAutocomplete(itemsAsOpts);
    };

    const getRows = async () => {
        const response = await api.getASNDetails(asnID, fbmAccount.fbmAccountName);
        if (response.status === false) {
            setNotification({ text: `No items found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        setRows(
            (response.data.output.items ?? []).map((item) => {
                if (item.qty !== null && item.qty !== undefined) {
                    if (item.unitsPerCase !== null && item.unitsPerCase !== undefined) {
                        return { ...item, totalUnits: item.qty * item.unitsPerCase };
                    }
                    return { ...item, totalUnits: item.qty * item.baseUnitsPerCase };
                }
                return item;
            }),
        );
    };

    const columns = [
        { accessor: 'id', Header: 'ID' },
        { accessor: 'sku', Header: 'Item SKU' },
        { accessor: 'externalSku', Header: 'External SKU' },
        { accessor: 'itemName', Header: 'Name' },
        {
            accessor: 'expectedExpirationDate',
            Header: 'Expected Expiration',
            editable: true,
            editProps: {
                type: 'input',
                inputType: 'date',
                required: true,
            },
        },
        {
            accessor: 'qty',
            Header: 'Num of Cases',
            editable: true,
            editProps: {
                type: 'input',
                inputType: 'number',
                required: true,
            },
        },
        {
            accessor: 'unitsPerCase',
            Header: 'Units Per Case',
            editable: true,
            editProps: {
                type: 'input',
                inputType: 'number',
                required: false,
            },
        },
        { accessor: 'csPallet', Header: 'Cases per pallet', editable: true },
        {
            accessor: 'numOfPallets',
            Header: 'Num Of Pallets',
            editable: true,
            editProps: { type: 'input', inputType: 'number', integer: true },
        },
        {
            accessor: 'totalUnits',
            Header: 'Num of Units',
            editable: false,
        },
        { accessor: 'partnerLotNumber', Header: 'Partner Lot Number', editable: true },
        { accessor: 'requireInspectionUponReceipt', Header: 'Require Inspecton', editable: true, type: 'checkbox' },
        {
            accessor: 'remove',
            Header: 'Remove',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <Button
                    id={`Remove_${row.id}`}
                    onClick={async () => {
                        const response = await api.removeASNItem(asnID, row.original.id, fbmAccount.fbmAccountName);

                        if (response.status === false) {
                            setNotification({ text: `Can't remove item ${response.msg}`, level: 'error' });
                            return;
                        }
                        onEditRow();
                        getRows();
                    }}
                >
                    Remove
                </Button>
            ),
        },
    ];

    useEffect(() => {
        getItems();
        getRows();
    }, []);

    return (
        <div>
            <Notifications options={notification} />
            {addItemModalOpen && (
                <AddItemModal
                    fbmAccount={fbmAccount}
                    getRows={() => {
                        onEditRow();
                        getRows();
                    }}
                    parentRow={parentRow}
                    asnID={asnID}
                    items={itemsAutocomplete}
                    closeModal={() => {
                        setAddItemModalOpen(false);
                    }}
                />
            )}
            <DataTable
                columns={columns}
                data={rows}
                editable
                saveEdit={async ({ field, id, value }) => {
                    const updates = { [field]: value };
                    updates.fbmAccount = fbmAccount.fbmAccountName;
                    const response = await api.modifyASNItem(asnID, id, updates);
                    if (response.status === false) {
                        let message = 'Error modifying ASN';
                        if (response) {
                            message += `: ${response.msg}`;
                        }
                        setNotification({ text: message, level: 'error' });
                        return false;
                    }
                    onEditRow();
                    getRows();
                    return true;
                }}
            />
            <Button
                onClick={() => {
                    setAddItemModalOpen(true);
                }}
            >
                Add Item
            </Button>
        </div>
    );
}

function EditDeliveryModal({ closeModal, fbmAccount, getRows, rowData }) {
    const [error, setError] = useState(null);

    const date = dayjs(rowData.scheduledDeliveryDate).format('YYYY-MM-DD');
    const time = dayjs(rowData.scheduledDeliveryDate).format('HH:mm');

    const formFields = [
        {
            name: 'asnID',
            initialValue: rowData.id,
            inputElement: 'textField',
            label: 'ASN ID',
            gridValue: 4,
            inputProps: { readOnly: true },
        },
        {
            name: 'scheduledDeliveryDate',
            initialValue: date,
            inputElement: 'date',
            label: 'Scheduled Delivery Date',
            gridValue: 4,
            inputProps: { required: true },
        },
        {
            name: 'scheduledTime',
            initialValue: time,
            inputElement: 'time',
            label: 'Delivery time',
            gridValue: 4,
            inputProps: { required: true },
        },
    ];

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '700px', margin: '10% auto', padding: '10px' }}>
                <FormComponent
                    formFields={formFields}
                    onSubmit={async (formData) => {
                        const item = {};
                        item.scheduledDeliveryDate = formData.scheduledDeliveryDate;
                        item.deliverTime = formData.scheduledTime;
                        item.fbmAccount = fbmAccount.fbmAccountName;

                        const response = await api.modifyASN(formData.asnID, item);
                        if (response.status === false) {
                            let message = 'Error modifying ASN';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setError(message);
                        }

                        closeModal();
                        getRows();
                    }}
                />
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </div>
        </Modal>
    );
}

function EditSpecialInstructionsModal({ closeModal, fbmAccount, rowData }) {
    const [error, setError] = useState(null);

    const { specialInstructions } = rowData;

    const formFields = [
        {
            name: 'asnID',
            initialValue: rowData.id,
            inputElement: 'textField',
            label: 'ASN ID',
            gridValue: 4,
            inputProps: { readOnly: true },
        },
        {
            name: 'specialInstructions',
            initialValue: specialInstructions,
            inputElement: 'textField',
            label: 'Special Instructions',
            gridValue: 8,
        },
    ];

    return (
        <Modal open onClose={closeModal}>
            <div style={{ outline: 0, backgroundColor: 'white', width: '700px', margin: '10% auto', padding: '10px' }}>
                <FormComponent
                    formFields={formFields}
                    onSubmit={async (formData) => {
                        const item = {};
                        item.specialInstructions = formData.specialInstructions;
                        item.fbmAccount = fbmAccount.fbmAccountName;

                        const response = await api.modifyASN(formData.asnID, item);
                        if (response.status === false) {
                            let message = 'Error modifying ASN';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setError(message);
                            return;
                        }

                        closeModal();
                    }}
                />
                {error && <div style={{ color: 'red' }}>{error}</div>}
            </div>
        </Modal>
    );
}

function CancelPOModal({ closeModal, fbmAccount, rowData, setNotification }) {
    return (
        <Modal open onClose={closeModal}>
            <div
                style={{
                    outline: 0,
                    backgroundColor: 'white',
                    width: '300px',
                    margin: '10% auto',
                    padding: '10px',
                    textAlign: 'center',
                }}
            >
                <div style={{ alignItems: 'center' }}>
                    <h2>Are you sure you want to cancel?</h2>
                    <br />
                    <div>
                        <Button
                            onClick={async () => {
                                const response = await api.cancelASN(rowData.original.id, fbmAccount.fbmAccountName);
                                if (response.status === false) {
                                    setNotification({ text: `Failed to cancel ${response.msg}`, level: 'error' });
                                    return;
                                }

                                setNotification({ text: 'ASN cancelled', level: 'success' });
                                closeModal();
                            }}
                        >
                            YES
                        </Button>
                        <Button onClick={() => closeModal()}>NO</Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

// The main PO view
function ViewASN() {
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [editDeliveryModalOpen, setEditDeliveryModalOpen] = useState(false);
    const [editSpecialInstructionsModalOpen, setEditSpecialInstructionsModalOpen] = useState(false);
    const [cancelPOModalOpen, setCancelPOModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [shippers, setShippers] = useState([]);
    const { fbmAccount, user } = useContext(UserContext);

    // Load purchase orders
    const getRows = async () => {
        const response = await api.getASNs(fbmAccount.fbmAccountName);

        if (response.status === false) {
            setNotification({ text: `Can't access ASNs: ${response.msg}`, level: 'error' });
            return;
        }

        const rows = (response.data?.rows ?? []).map((row) => ({
            ...row,
            createdAt: dayjs(row.createdAt.date).format('MM/DD/YYYY'),
            receivedDate: row.receivedDate ? dayjs(row.receivedDate.date).format('MM/DD/YYYY HH:mm:ss') : '',
            scheduledDeliveryDate: dayjs(row.scheduledDeliveryDate.date).format('MM/DD/YYYY HH:mm:ss'),
        }));

        setRows(rows);
    };

    useEffect(() => {
        if (fbmAccount?.id) {
            getRows();
            getShippers();
        }
    }, [fbmAccount]);

    // Load shippers
    let getShippers = async () => {
        const response = await procureApi.getShippers();
        if (response.status === false) {
            setNotification({ text: `No shippers found ${response.msg}`, level: 'error' });
            setShippers([]);
            return;
        }

        const allowedShipperIDs = fbmAccount.inboundShipperIDs;

        const shippersAsOpts = response.data.rows
            .map((row) => {
                row.text = row.name;
                row.value = row.id;
                return row;
            })
            .filter((item) => allowedShipperIDs.includes(item.value));

        const pleaseSelect = { text: 'Please Select', value: 0 };
        setShippers([pleaseSelect].concat(shippersAsOpts));
    };

    const closeDeliveryEditModal = () => {
        setEditDeliveryModalOpen(false);
    };

    const showDeliveryEditModal = (row, closeDeliveryEditModal) => {
        setSelectedRow(row);
        setEditDeliveryModalOpen(true);
    };

    const closeSpecialInstructionsEditModal = () => {
        setEditSpecialInstructionsModalOpen(false);
    };

    const showSpecialInstructionsEditModal = (row, closeSpecialInstructionsEditModal) => {
        setSelectedRow(row);
        setEditSpecialInstructionsModalOpen(true);
    };

    const showCancelPOModal = (row) => {
        setSelectedRow(row);
        setCancelPOModalOpen(true);
    };

    const columns = [
        { accessor: 'id', Header: 'ASN ID' },
        { accessor: 'facilityName', Header: 'Facility' },
        { accessor: 'createdAt', Header: 'Created On' },
        { accessor: 'status', Header: 'Status' },
        {
            accessor: 'scheduledDeliveryDate',
            Header: 'Scheduled Delivery',
            sortType: 'datetimeString',
            editable: true,
            customEdit: showDeliveryEditModal,
        },
        {
            accessor: 'receivedDate',
            Header: 'Received Date',
            editable: false,
            Cell: ({ column, editable, row, saveEdit }) => {
                if (row.values?.status === 'RECEIVED' && row.values?.receivedDate) {
                    return row.values.receivedDate;
                }

                return '';
            },
        },
        {
            accessor: 'shipperID',
            Header: 'Shipper',
            editable: true,
            editProps: {
                type: 'select',
                options: shippers,
            },
        },

        {
            accessor: 'minTempReq',
            Header: 'Min Temperature',
            editable: true,
            editProps: {
                type: 'input',
                inputType: 'number',
            },
        },
        {
            accessor: 'maxTempReq',
            Header: 'Max Temperature',
            editable: true,
            editProps: {
                type: 'input',
                inputType: 'number',
            },
        },
        {
            accessor: 'totalPallets',
            Header: 'Total Pallets',
            editable: false,
        },
        {
            accessor: 'shippingRef',
            Header: 'Reference Number',
            editable: true,
            editProps: {
                type: 'input',
                inputType: 'text',
            },
            width: '150px',
        },
        {
            accessor: 'specialInstructions',
            Header: 'Special Instructions',
            editable: true,
            customEdit: showSpecialInstructionsEditModal,
            width: '150px',
        },
        {
            accessor: 'download',
            hiddenColumn: user.fbm,
            Header: 'Download',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => {
                const [downloadOpt, setDownloadOpt] = useState('csv');
                const downloadOptions = ['csv', 'pdf', 'ship-list'];
                return (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <NativeSelect
                            id={`Download_Select_${row.id}`}
                            className="input-element-select"
                            value={downloadOpt}
                            label="Download"
                            onChange={(event) => {
                                setDownloadOpt(event.target.value);
                            }}
                        >
                            {downloadOptions.map((value, i) => (
                                <option key={i} value={value}>
                                    {value}
                                </option>
                            ))}
                            ;
                        </NativeSelect>
                        <Button
                            id={`Download_${row.id}`}
                            onClick={() => {
                                if (downloadOpt === 'csv') {
                                    procureApi.downloadPOCSV(row.original.id);
                                } else if (downloadOpt === 'pdf') {
                                    procureApi.downloadPOPDF(row.original.id);
                                } else if (downloadOpt === 'ship-list') {
                                    procureApi.downloadPOShipList(row.original.id);
                                } else {
                                    console.log(`Unrecognized download option ${downloadOpt}`);
                                }
                            }}
                        >
                            Download
                        </Button>
                    </div>
                );
            },
        },
        {
            accessor: 'cancel',
            Header: 'Cancel',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        id={`Cancel_${row.id}`}
                        onClick={async () => {
                            showCancelPOModal(row);
                        }}
                    >
                        Cancel
                    </Button>
                </div>
            ),
        },
    ];

    return (
        <div>
            <h1>{fbmAccount.companyName} - Advance Shipping Notifications</h1>
            <Notifications options={notification} />
            {}
            <div>
                {editDeliveryModalOpen && (
                    <EditDeliveryModal
                        rowData={selectedRow}
                        getRows={getRows}
                        closeModal={() => {
                            setEditDeliveryModalOpen(false);
                        }}
                        fbmAccount={fbmAccount}
                    />
                )}
                {editSpecialInstructionsModalOpen && (
                    <EditSpecialInstructionsModal
                        rowData={selectedRow}
                        closeModal={() => {
                            setEditSpecialInstructionsModalOpen(false);
                            getRows();
                        }}
                        fbmAccount={fbmAccount}
                    />
                )}
                {cancelPOModalOpen && (
                    <CancelPOModal
                        fbmAccount={fbmAccount}
                        rowData={selectedRow}
                        setNotification={setNotification}
                        closeModal={() => {
                            setCancelPOModalOpen(false);
                            getRows();
                        }}
                    />
                )}
                <DataTable
                    columns={columns}
                    data={rows}
                    initialSortBy={[{ id: 'scheduledDeliveryDate', desc: true }]}
                    expandable
                    ExpansionComponent={<ViewItemsTable fbmAccount={fbmAccount} onEditRow={getRows} />}
                    editable
                    saveEdit={async ({ field, id, value }) => {
                        const item = {};
                        item[field] = value;
                        item.fbmAccount = fbmAccount.fbmAccountName;

                        const response = await api.modifyASN(id, item);
                        if (response.status === false) {
                            let message = 'Error modifying ASN';
                            if (response) {
                                message += `: ${response.msg}`;
                            }
                            setNotification({ text: message, level: 'error' });
                        }

                        await getRows();
                        return response.status;
                    }}
                />
            </div>
        </div>
    );
}

export { ViewASN, ViewItemsTable };
